export interface PublishChecklistInput {
  /** Branding has a logo or custom primary colour set. */
  hasBranding: boolean;
  hasHours: boolean;
  itemCount: number;
  payInStoreEnabled: boolean;
  stripeChargesEnabled: boolean;
  reservationsEnabled: boolean;
  printerCount: number;
}

export interface ChecklistItem {
  key: 'branding' | 'hours' | 'menu' | 'payments' | 'printer';
  label: string;
  detail: string;
  done: boolean;
}

/**
 * "Ready to publish" checklist. Mirrors the server-side publish validation in
 * `getPublishChecklist`: hours, at least one item, and a payment method. Branding
 * and a paired printer are advisory (they do not block publishing server-side).
 */
export function computePublishChecklist(input: PublishChecklistInput): ChecklistItem[] {
  const hasPaymentMethod =
    input.payInStoreEnabled || input.stripeChargesEnabled || input.reservationsEnabled;

  return [
    {
      key: 'branding',
      label: 'Add your branding',
      detail: 'Upload a logo and pick your colours.',
      done: input.hasBranding,
    },
    {
      key: 'hours',
      label: 'Set opening hours',
      detail: 'Add at least one weekly opening range.',
      done: input.hasHours,
    },
    {
      key: 'menu',
      label: 'Build your menu',
      detail: 'Add at least one menu item.',
      done: input.itemCount > 0,
    },
    {
      key: 'payments',
      label: 'Enable a payment method',
      detail: 'Turn on pay-in-store or connect Stripe for card payments.',
      done: hasPaymentMethod,
    },
    {
      key: 'printer',
      label: 'Pair a printer',
      detail: 'Pair a printer so tickets print automatically.',
      done: input.printerCount > 0,
    },
  ];
}

/** Server-side publish requirements: hours, a payment method, and — when online
 * ordering is enabled — at least one menu item. Reservations-only locations do
 * not need a menu. */
export function isReadyToPublish(items: ChecklistItem[], orderingEnabled = true): boolean {
  const required = new Set<ChecklistItem['key']>(['hours', 'payments']);
  if (orderingEnabled) required.add('menu');
  return items.filter((item) => required.has(item.key)).every((item) => item.done);
}

export function checklistProgress(items: ChecklistItem[]): number {
  if (items.length === 0) return 0;
  return Math.round((items.filter((item) => item.done).length / items.length) * 100);
}
